/**
 * Modal Variants Configuration
 * Style configurations for different modal variants and sizes
 */

// =============================================================================
// Modal Size Configuration
// =============================================================================

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

export interface ModalSizeConfig {
  maxWidth: string;
  width: string;
  height?: string;
  padding: string;
}

export const modalSizeConfigs: Record<ModalSize, ModalSizeConfig> = {
  xs: {
    maxWidth: 'max-w-xs',
    width: 'w-full',
    padding: 'p-3',
  },
  sm: {
    maxWidth: 'max-w-md',
    width: 'w-full',
    padding: 'p-4',
  },
  md: {
    maxWidth: 'max-w-2xl',
    width: 'w-full',
    padding: 'p-4',
  },
  lg: {
    maxWidth: 'max-w-4xl',
    width: 'w-full',
    padding: 'p-4',
  },
  xl: {
    maxWidth: 'max-w-6xl',
    width: 'w-full',
    padding: 'p-4',
  },
  '2xl': {
    maxWidth: 'max-w-7xl',
    width: 'w-full',
    padding: 'p-4',
  },
  full: {
    maxWidth: 'max-w-screen-xl',
    width: 'w-full mx-4',
    height: 'h-[90vh]',
    padding: 'p-0',
  },
};

/**
 * Get size classes for modal
 * @param size - Modal size
 * @returns Size configuration
 */
export const getModalSizeClasses = (size: ModalSize): ModalSizeConfig => {
  return modalSizeConfigs[size] || modalSizeConfigs.md;
};

// =============================================================================
// Modal Variant Configuration
// =============================================================================

export type ModalVariant = 'default' | 'admin' | 'user' | 'success' | 'warning' | 'danger' | 'info';

export interface ModalVariantConfig {
  backdrop: string;
  modal: string;
  header: string;
  title: string;
  closeButton: string;
  border: string;
  focus: string;
}

export const modalVariantConfigs: Record<ModalVariant, ModalVariantConfig> = {
  default: {
    backdrop: 'bg-black/50',
    modal: 'bg-white border-gray-200 shadow-2xl',
    header: 'border-gray-200 bg-gray-50',
    title: 'text-gray-900',
    closeButton: 'text-gray-600 hover:text-gray-800 hover:bg-gray-100',
    border: 'border-gray-200',
    focus: 'focus:ring-2 focus:ring-blue-500',
  },
  admin: {
    backdrop: 'bg-blue-900/20',
    modal: 'bg-white border-blue-200 shadow-2xl shadow-blue-500/10',
    header: 'border-blue-100 bg-blue-50',
    title: 'text-blue-900 font-semibold',
    closeButton: 'text-blue-600 hover:text-blue-800 hover:bg-blue-100',
    border: 'border-blue-200',
    focus: 'focus:ring-2 focus:ring-blue-500',
  },
  user: {
    backdrop: 'bg-green-900/20',
    modal: 'bg-white border-green-200 shadow-2xl shadow-green-500/10',
    header: 'border-green-100 bg-green-50',
    title: 'text-green-900 font-semibold',
    closeButton: 'text-green-600 hover:text-green-800 hover:bg-green-100',
    border: 'border-green-200',
    focus: 'focus:ring-2 focus:ring-green-500',
  },
  success: {
    backdrop: 'bg-emerald-900/20',
    modal: 'bg-white border-emerald-200 shadow-2xl shadow-emerald-500/10',
    header: 'border-emerald-100 bg-emerald-50',
    title: 'text-emerald-900 font-semibold',
    closeButton: 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100',
    border: 'border-emerald-200',
    focus: 'focus:ring-2 focus:ring-emerald-500',
  },
  warning: {
    backdrop: 'bg-amber-900/20',
    modal: 'bg-white border-amber-200 shadow-2xl shadow-amber-500/10',
    header: 'border-amber-100 bg-amber-50',
    title: 'text-amber-900 font-semibold',
    closeButton: 'text-amber-600 hover:text-amber-800 hover:bg-amber-100',
    border: 'border-amber-200',
    focus: 'focus:ring-2 focus:ring-amber-500',
  },
  danger: {
    backdrop: 'bg-red-900/20',
    modal: 'bg-white border-red-200 shadow-2xl shadow-red-500/10',
    header: 'border-red-100 bg-red-50',
    title: 'text-red-900 font-semibold',
    closeButton: 'text-red-600 hover:text-red-800 hover:bg-red-100',
    border: 'border-red-200',
    focus: 'focus:ring-2 focus:ring-red-500',
  },
  info: {
    backdrop: 'bg-sky-900/20',
    modal: 'bg-white border-sky-200 shadow-2xl shadow-sky-500/10',
    header: 'border-sky-100 bg-sky-50',
    title: 'text-sky-900 font-semibold',
    closeButton: 'text-sky-600 hover:text-sky-800 hover:bg-sky-100',
    border: 'border-sky-200',
    focus: 'focus:ring-2 focus:ring-sky-500',
  },
};

/**
 * Get variant classes for modal
 * @param variant - Modal variant
 * @returns Variant configuration
 */
export const getModalVariantClasses = (variant: ModalVariant): ModalVariantConfig => {
  return modalVariantConfigs[variant] || modalVariantConfigs.default;
};

// =============================================================================
// Button Variant Configuration
// =============================================================================

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'ghost';

export interface ButtonVariantConfig {
  base: string;
  normal: string;
  hover: string;
  active: string;
  disabled: string;
  focus: string;
}

export const buttonVariantConfigs: Record<ButtonVariant, ButtonVariantConfig> = {
  primary: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-blue-600 text-white',
    hover: 'hover:bg-blue-700 hover:shadow-md',
    active: 'active:bg-blue-800',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
  },
  secondary: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-gray-200 text-gray-900 border border-gray-300',
    hover: 'hover:bg-gray-300 hover:shadow-md',
    active: 'active:bg-gray-400',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
  },
  success: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-green-600 text-white',
    hover: 'hover:bg-green-700 hover:shadow-md',
    active: 'active:bg-green-800',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
  },
  warning: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-yellow-600 text-white',
    hover: 'hover:bg-yellow-700 hover:shadow-md',
    active: 'active:bg-yellow-800',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2',
  },
  danger: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-red-600 text-white',
    hover: 'hover:bg-red-700 hover:shadow-md',
    active: 'active:bg-red-800',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
  },
  info: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-sky-600 text-white',
    hover: 'hover:bg-sky-700 hover:shadow-md',
    active: 'active:bg-sky-800',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-sky-500 focus:ring-offset-2',
  },
  ghost: {
    base: 'px-4 py-2 rounded-lg font-medium transition-all duration-200',
    normal: 'bg-transparent text-gray-700 border border-gray-300',
    hover: 'hover:bg-gray-50 hover:text-gray-900',
    active: 'active:bg-gray-100',
    disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
    focus: 'focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
  },
};

/**
 * Get complete button classes
 * @param variant - Button variant
 * @returns Complete button class string
 */
export const getButtonClasses = (variant: ButtonVariant): string => {
  const config = buttonVariantConfigs[variant] || buttonVariantConfigs.primary;
  return `${config.base} ${config.normal} ${config.hover} ${config.active} ${config.disabled} ${config.focus}`;
};

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Combine modal classes with custom classes
 * @param baseClasses - Base classes from variant
 * @param customClasses - Custom classes to add
 * @returns Combined class string
 */
export const combineModalClasses = (baseClasses: string, customClasses?: string): string => {
  return customClasses ? `${baseClasses} ${customClasses}` : baseClasses;
};

/**
 * Get responsive modal size based on screen size
 * @param defaultSize - Default size
 * @param mobileSize - Mobile size override
 * @returns Responsive size classes
 */
export const getResponsiveModalSize = (defaultSize: ModalSize, mobileSize?: ModalSize): string => {
  const defaultConfig = getModalSizeClasses(defaultSize);
  const mobileConfig = mobileSize ? getModalSizeClasses(mobileSize) : defaultConfig;

  return `${mobileConfig.maxWidth} sm:${defaultConfig.maxWidth}`;
};

/**
 * Check if modal should be fullscreen on mobile
 * @param size - Modal size
 * @returns Boolean indicating if fullscreen on mobile
 */
export const shouldBeFullscreenOnMobile = (size: ModalSize): boolean => {
  return ['lg', 'xl', '2xl', 'full'].includes(size);
};
